<script setup>
import { computed } from 'vue';
import { describeRow, importedLine } from '../../helpers/notRegisteredRow';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlListRow from '../xl/XlListRow.vue';
import IconTriangleWarning from '../icons/IconTriangleWarning.vue';
import XlTag from '../xl/XlTag.vue';

const STALE_AFTER_DAYS = 14;
const DAY_MS = 86400000;

const props = defineProps({
  rows: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
  showBroker: { type: Boolean, default: false },
  brokerNameById: { type: Function, default: () => '' },
  neverTold: { type: Number, default: 0 },
  filtered: { type: Boolean, default: false },
});

const emit = defineEmits(['resend', 'revoke', 'open']);

const nameOf = (row) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.email;

const described = computed(() => props.rows.map((row) => ({ row, ...describeRow(row) })));

const staleCount = computed(() => props.rows.filter((row) => row.kind === 'invited'
  && (Date.now() - new Date(row.happened_at).getTime()) / DAY_MS > STALE_AFTER_DAYS).length);
</script>

<template>
  <div class="invitations">
    <p class="invitations__lead">People who can reach the portal but haven't registered yet — some invited, some given access without an invitation.</p>

    <XlCard v-if="props.neverTold > 0" padded tone="warning" class="invitations__note" data-testid="never-told-note">
      <span class="invitations__note-icon invitations__note-icon--warning"><IconTriangleWarning color="currentColor" /></span>
      <div>
        <b>{{ props.neverTold === 1 ? '1 person has' : `${props.neverTold} people have` }} access but {{ props.neverTold === 1 ? 'has' : 'have' }} never been told.</b>
        They can't register until somebody sends the invitation.
      </div>
    </XlCard>

    <XlCard v-if="props.rows.length" class="invitations__list">
      <XlListRow v-for="item in described" :key="item.row.id" data-testid="invitation-row">
        <template #leading>
          <XlAvatar class="invitations__avatar" :name="nameOf(item.row)" :size="38" />
        </template>
        <template #title>
          <button type="button" class="invitations__name" data-testid="invitation-open" @click="emit('open', item.row)">{{ nameOf(item.row) }}</button>
        </template>
        <template #subtitle>
          {{ item.line }}
          <span class="invitations__imported" data-testid="imported-details">{{ importedLine(item.row) }}</span>
        </template>
        <template #trailing>
          <div class="invitations__actions">
            <XlTag :tone="item.tagTone">{{ item.tag }}</XlTag>
            <XlTag v-if="props.showBroker && props.brokerNameById(item.row.broker_id)">{{ props.brokerNameById(item.row.broker_id) }}</XlTag>
            <XlButton :variant="item.primaryVariant" size="sm" :disabled="props.busy" @click="emit('resend', item.row)">
              {{ item.primary }}
            </XlButton>
            <button
              type="button"
              class="invitations__cancel"
              data-testid="cancel-invitation"
              :disabled="props.busy"
              :title="item.secondaryLabel"
              :aria-label="item.secondaryAria"
              @click="emit('revoke', item.row)"
            >✕</button>
          </div>
        </template>
      </XlListRow>
    </XlCard>

    <XlCard v-else padded class="invitations__empty">
      <p>{{ props.filtered ? 'Nobody matches that search.' : 'Everyone with access has registered.' }}</p>
    </XlCard>

    <XlCard v-if="staleCount" padded class="invitations__note" data-testid="stale-note">
      <span class="invitations__note-icon"><IconTriangleWarning color="currentColor" /></span>
      <div>
        <b>{{ staleCount === 1 ? 'One invitation is' : `${staleCount} invitations are` }} over two weeks old.</b>
        People rarely act on an invitation after a fortnight — a phone call usually works better than
        sending it again.
      </div>
    </XlCard>
  </div>
</template>

<style scoped>
.invitations {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--xl-font-family);
}

.invitations__lead {
  margin: 0;
  font-size: 13.5px;
  color: var(--xl-color-outline);
}

.invitations__list { margin: 0; padding: 0 22px; }

/* Nobody has arrived yet, so the face is a placeholder rather than a person. */
.invitations__avatar { opacity: 0.6; }

.invitations__name {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.invitations__name:hover { text-decoration: underline; }

.invitations__imported { display: block; }

.invitations__empty {
  margin: 0;
  text-align: center;
}

.invitations__empty p {
  margin: 0;
  padding: 12px 0;
  font-size: var(--xl-font-size-base);
  color: var(--xl-color-outline);
}

.invitations__note {
  display: flex;
  gap: 11px;
  margin: 0;
  font-size: 13.5px;
  color: var(--xl-color-secondary-text);
}

.invitations__note-icon { display: flex; flex: 0 0 18px; color: var(--xl-color-outline); }
.invitations__note-icon--warning { color: var(--xl-color-warning); }
.invitations__note-icon :deep(svg) { width: 18px; height: 18px; }

.invitations__actions { display: contents; }

@media (max-width: 620px) {
  .invitations :deep(.xl-list-row__text) { overflow-wrap: anywhere; }

  .invitations__actions {
    display: flex;
    flex: 1 0 100%;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    padding-left: 52px;
  }
}

.invitations__cancel {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  background: transparent;
  color: var(--xl-color-error);
  line-height: 1;
  cursor: pointer;
}

.invitations__cancel:hover:not(:disabled) {
  background: color-mix(in srgb, var(--xl-color-error) 8%, white);
}

.invitations__cancel:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
